import * as rd from '@devexperts/remote-data-ts'

import { DateTime } from 'luxon'
import { GroupIds } from '@/models/service'
import { resetAllState } from '@/store/commonActions.ts'
import { INFINITY_FALLBACK_VALUE } from '@/utils/constants'
import { isBrowserError } from '@/utils/typeGuards.ts'
import { httpClient } from '../lib/http/HttpClient'
import { AppMode } from '../models/app'
import {
  BookingAvailabilityError,
  BookingAvailabilityResponse,
  BookingAvailabilityResponseError,
} from '../models/bookingAvailability'
import { getHalfDayValueFromBookingPeriod } from '../utils/halfDay'

import { appSlice } from './appSlice'
import { RootState } from './configureStore'
import { createAppSlice } from './createAppSlice'
import {
  bookingPeriodSelector,
  periodAsUnixTimeSelector,
} from './insertPeriodSlice'
import { licenseSlice } from './licenseSlice'
import { sectorRulesPayloadSelector } from './sectorRulesSlice/selectors/sectorRulesPayloadSelector'
import { servicesSlice } from './servicesSlice'

export enum BookingFlow {
  SPOTS = 0,
  TICKETS = 1,
}

export enum BookingAvailability {
  NONE = 'none',
  SPOTS = 'spots',
  TICKETS = 'tickets',
  SPOTS_AND_TICKETS = 'spots-and-tickets',
}

export type BookingAvailabilitySliceState = rd.RemoteData<
  {
    code: BookingAvailabilityError
    substitutions: Record<string, string>
  },
  | {
      availableSpots: number
      type: BookingAvailability.SPOTS
    }
  | {
      availableTickets: number
      type: BookingAvailability.TICKETS
    }
  | {
      availableTickets: number
      availableSpots: number
      type: BookingAvailability.SPOTS_AND_TICKETS
    }
  | {
      type: BookingAvailability.NONE
    }
>

const initialState: BookingAvailabilitySliceState = rd.initial

export const bookingAvailabilitySlice = createAppSlice({
  extraReducers(builder) {
    builder.addCase(resetAllState, () => initialState)
  },
  initialState,
  name: 'bookingAvailability',
  reducers: (create) => ({
    load: create.asyncThunk<
      {
        appMode: AppMode
        bookingFlow: BookingFlow | null
        data: BookingAvailabilityResponse
        groupIds: GroupIds
      },
      { bookingFlow: BookingFlow | null },
      {
        rejectValue: {
          code: BookingAvailabilityError
          substitutions?: Record<string, string>
        }
      }
    >(
      async (
        payload,
        thunkApi
      ): Promise<{
        appMode: AppMode
        bookingFlow: BookingFlow | null
        data: BookingAvailabilityResponse
        groupIds: GroupIds
      }> => {
        const rootState = thunkApi.getState() as RootState
        const license = licenseSlice.selectors.licenseCode(rootState)
        const period = periodAsUnixTimeSelector(rootState)
        const appMode = appSlice.selectors.mode(rootState)
        const channel = appSlice.selectors.channel(rootState)
        const country = appSlice.selectors.country(rootState)
        const groupIds = servicesSlice.selectors.groupIds(rootState)
        const bookingPeriod = bookingPeriodSelector(rootState)
        const sectorRuleFields = sectorRulesPayloadSelector(rootState, period)

        if (!period) {
          throw thunkApi.rejectWithValue({
            code: BookingAvailabilityError.PERIOD_NOT_FOUND,
          })
        }
        const now = DateTime.local({ zone: 'utc' })
          .startOf('day')
          .toUnixInteger()
        if (period.start < now || period.end < now) {
          throw thunkApi.rejectWithValue({
            code: BookingAvailabilityError.PAST_DATE,
          })
        }

        const res = await httpClient.fetch<
          BookingAvailabilityResponse,
          BookingAvailabilityResponseError
        >(`beaches/${license}/booking-availability`, country, {
          body: JSON.stringify({
            channel,
            from: period.start,
            halfDay: getHalfDayValueFromBookingPeriod(bookingPeriod),
            to: period.end,
            ...(groupIds !== null ? { groupIds } : {}),
            ...(sectorRuleFields !== null ? { sectorRuleFields } : {}),
          }),
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
        })

        if (res.status === 'error') {
          if (isBrowserError(res.error)) {
            throw thunkApi.rejectWithValue({
              code: BookingAvailabilityError.GENERIC_ERROR,
              substitutions: {},
            })
          }

          throw thunkApi.rejectWithValue({
            code:
              res.error.internal_error_code ??
              BookingAvailabilityError.GENERIC_ERROR,
            substitutions: res.error.error_ex?.substitution ?? {},
          })
        }

        return {
          appMode,
          bookingFlow: payload.bookingFlow,
          data: res.data,
          groupIds,
        }
      },
      {
        fulfilled: (_state, action) => {
          const data = action.payload.data
          const result = data.result
          const availableSpots = result.mapAvailability.hasMapLimit
            ? result.mapAvailability.availableSpots
            : INFINITY_FALLBACK_VALUE
          const availableTickets = result.serviceAvailability.hasServiceLimit
            ? result.serviceAvailability.availableTickets
            : INFINITY_FALLBACK_VALUE

          if (
            action.payload.groupIds !== null &&
            action.payload.groupIds.length > 0
          ) {
            // only check tickets, cannot go to map
            if (result.serviceAvailability.hasServices) {
              return rd.success({
                availableTickets,
                type: BookingAvailability.TICKETS,
              })
            } else {
              return rd.failure({
                code: BookingAvailabilityError.SOLD_OUT,
                substitutions: {},
              })
            }
          }

          if (
            result.mapAvailability.hasSpots &&
            result.serviceAvailability.hasServices
          ) {
            if (
              action.payload.appMode === AppMode.WEBSITE &&
              action.payload.bookingFlow === BookingFlow.SPOTS
            ) {
              return rd.success({
                availableSpots,
                type: BookingAvailability.SPOTS,
              })
            } else if (
              action.payload.appMode === AppMode.WEBSITE &&
              action.payload.bookingFlow === BookingFlow.TICKETS
            ) {
              return rd.success({
                availableTickets,
                type: BookingAvailability.TICKETS,
              })
            } else if (action.payload.bookingFlow === null) {
              return rd.success({
                availableSpots,
                availableTickets,
                type: BookingAvailability.SPOTS_AND_TICKETS,
              })
            }
          } else if (result.mapAvailability.hasSpots) {
            return rd.success({
              availableSpots,
              type: BookingAvailability.SPOTS,
            })
          } else if (result.serviceAvailability.hasServices) {
            return rd.success({
              availableTickets,
              type: BookingAvailability.TICKETS,
            })
          } else {
            return rd.failure({
              code: BookingAvailabilityError.SOLD_OUT,
              substitutions: {},
            })
          }
        },
        pending: () => rd.pending,
        rejected: (_, action) => {
          if (!action.payload) {
            return rd.failure({
              code: BookingAvailabilityError.GENERIC_ERROR,
              substitutions: {},
            })
          }
          return rd.failure({
            code: action.payload.code,
            substitutions: action.payload.substitutions ?? {},
          })
        },
      }
    ),
  }),

  selectors: {
    areSpotsAvailable: (state) =>
      rd.isSuccess(state) &&
      (state.value.type === BookingAvailability.SPOTS ||
        state.value.type === BookingAvailability.SPOTS_AND_TICKETS) &&
      state.value.availableSpots > 0,
    areTicketsAvailable: (state) =>
      rd.isSuccess(state) &&
      (state.value.type === BookingAvailability.TICKETS ||
        state.value.type === BookingAvailability.SPOTS_AND_TICKETS) &&
      state.value.availableTickets > 0,
    errorCode: (state) => (rd.isFailure(state) ? state.error.code : null),
    errorSubstitutions: (state) =>
      rd.isFailure(state) ? state.error.substitutions : null,
    isSoldOut(state): boolean {
      if (!rd.isSuccess(state)) {
        return true
      }

      switch (state.value.type) {
        case BookingAvailability.NONE:
          return true

        case BookingAvailability.SPOTS:
          return state.value.availableSpots === 0

        case BookingAvailability.TICKETS:
          return state.value.availableTickets === 0

        case BookingAvailability.SPOTS_AND_TICKETS:
          return (
            state.value.availableTickets === 0 &&
            state.value.availableSpots === 0
          )
      }
    },
    self: (state) => state,
  },
})
